import {
  AddIcon,
  Button,
  EmptyState,
  Grid,
  ListPanel,
  ListPanelHeader,
  MenuItem,
  SearchField,
  SelectField,
  SubTopicList,
  SubTopicListItemSkeleton,
  Tag,
} from '@all-manager/design-system-v2';
import type { StatusFilter, TopicListPage } from '../../pages/useTopicListPage';
import SubTopicRow from '../SubTopicRow/SubTopicRow';
import TopicsPagination from '../TopicsPagination/TopicsPagination';
import { useSubTopicListPanel } from './useSubTopicListPanel';

const SKELETON_ROWS = [0, 1, 2, 3];

type Props = {
  page: TopicListPage;
};

export default function SubTopicListPanel(props: Props) {
  const page = props.page;
  const hook = useSubTopicListPanel(page);
  const topic = page.selectedTopic;

  if (!page.topicId) {
    return (
      <ListPanel fill>
        <EmptyState
          title="Sélectionnez un sujet"
          subtitle="Ses sous-sujets s'afficheront ici."
        />
      </ListPanel>
    );
  }

  if (!topic) {
    return (
      <ListPanel fill>
        {page.isSelectedTopicLoading ? (
          <SubTopicList aria-label="Sous-sujets" aria-busy>
            {SKELETON_ROWS.map((row) => <SubTopicListItemSkeleton key={row} />)}
          </SubTopicList>
        ) : (
          <EmptyState
            title="Sujet introuvable"
            subtitle="Il a peut-être été supprimé."
            action={(
              <Button variant="text" size="small" href={page.listHref} onClick={hook.handleBackClick}>
                Retour aux sujets
              </Button>
            )}
          />
        )}
      </ListPanel>
    );
  }

  return (
    <ListPanel
      fill
      header={(
        <ListPanelHeader
          title={topic.name}
          status={topic.archived ? <Tag label="Archivé" color="default" variantIcon="state" /> : undefined}
          subtitle={page.subCountsLabel}
          backHref={page.listHref}
          onBackClick={hook.handleBackClick}
          backLabel="Retour aux sujets"
          action={page.isAdmin ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={hook.handleNewSubTopicClick}
            >
              Nouveau sous-sujet
            </Button>
          ) : undefined}
        />
      )}
      toolbar={page.hasSubTopics ? (
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <SearchField
              value={page.subSearch}
              onChange={page.setSubSearch}
              label={`Rechercher dans ${topic.name}`}
              placeholder="Rechercher un sous-sujet"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectField<StatusFilter>
              label="Statut"
              size="small"
              fullWidth
              value={page.subStatusFilter}
              onChange={hook.handleStatusFilterChange}
            >
              <MenuItem value="active">Actifs</MenuItem>
              <MenuItem value="archived">Archivés</MenuItem>
              <MenuItem value="all">Tous</MenuItem>
            </SelectField>
          </Grid>
        </Grid>
      ) : undefined}
      footer={page.showSubPagination ? (
        <TopicsPagination
          count={page.totalSubTopics}
          page={page.subPage}
          rowsPerPage={page.subPerPage}
          onPageChange={page.setSubPage}
          onRowsPerPageChange={page.setSubPerPage}
        />
      ) : undefined}
    >
      {page.isSubTopicsError ? (
        <EmptyState
          title="Sous-sujets indisponibles"
          subtitle="Le chargement a échoué. Rechargez la page pour réessayer."
        />
      ) : page.isSubTopicsLoading ? (
        <SubTopicList aria-label={`Sous-sujets de ${topic.name}`} aria-busy>
          {SKELETON_ROWS.map((row) => <SubTopicListItemSkeleton key={row} />)}
        </SubTopicList>
      ) : !page.hasSubTopics ? (
        <EmptyState
          title="Aucun sous-sujet"
          subtitle={page.isAdmin
            ? 'Ajoutez un sous-sujet pour suivre une affaire précise de ce sujet.'
            : 'Les sous-sujets de ce sujet apparaîtront ici.'}
        />
      ) : hook.isEmpty ? (
        <EmptyState
          title="Aucun résultat"
          subtitle={hook.noResultSubtitle}
          action={page.hasSubFilters ? (
            <Button variant="text" size="small" onClick={page.resetSubFilters}>
              Effacer les filtres
            </Button>
          ) : undefined}
        />
      ) : (
        <SubTopicList aria-label={`Sous-sujets de ${topic.name}`}>
          {page.subTopics.map((subTopic) => (
            <SubTopicRow key={subTopic.id} page={page} subTopic={subTopic} />
          ))}
        </SubTopicList>
      )}
    </ListPanel>
  );
}
